/* eslint-disable react/prop-types, no-unused-vars */

import { Button, List, Popconfirm, Divider } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import { TRANSFORM_TYPES } from './transformTypes';

const PipelineSidebar = ({
    steps,
    selectedStepIndex,
    showCatalog,
    savedPipelines,
    onAddClick,
    onSelectStep,
    onRemoveStep,
    onToggleStep,
    onAddTransform,
    onLoadPipeline,
    onDeletePipeline,
    onCloseCatalog,
}) => {
    return (
        <div className="flex w-80 flex-shrink-0 flex-col border-r border-gray-200 bg-gray-100">
            {/* Add Transformation Button */}
            <div className="p-4">
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    block
                    onClick={onAddClick}
                    data-testid="pipeline-add-transform-button"
                >
                    Add Transformation
                </Button>
            </div>
            <Divider className="m-0" />

            {/* Transformation Catalog */}
            {showCatalog && (
                <div className="max-h-96 overflow-auto border-b border-gray-200 bg-gray-100" data-testid="pipeline-catalog-list">
                    <List
                        size="small"
                        dataSource={TRANSFORM_TYPES}
                        renderItem={(item) => {
                            const IconComponent = item.icon;
                            return (
                                <List.Item
                                    className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                                    onClick={() => onAddTransform(item.type)}
                                    data-testid={`pipeline-catalog-item-${item.type}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <IconComponent className="text-xl text-blue-500 flex-shrink-0" />
                                        <div>
                                            <div className="text-sm font-semibold leading-tight">{item.label}</div>
                                            <div className="text-xs font-medium text-gray-600 leading-tight">{item.description}</div>
                                        </div>
                                    </div>
                                </List.Item>
                            );
                        }}
                    />
                </div>
            )}

            {/* Transformation Steps */}
            <div className="flex-1 overflow-auto p-4">
                <h4 className="mb-2 text-sm font-semibold text-gray-600">
                    Transformation Steps ({steps.length})
                </h4>
                {steps.length === 0 ? (
                    <p className="text-sm text-gray-400" data-testid="pipeline-empty-state">
                        No transformations added yet. Click &quot;Add Transformation&quot; to begin.
                    </p>
                ) : (
                    <div className="flex flex-col gap-1" data-testid="pipeline-step-list">
                        {steps.map((step, index) => {
                            const typeInfo = TRANSFORM_TYPES.find(t => t.type === step.type);
                            const IconComponent = typeInfo ? typeInfo.icon : null;
                            const isSelected = selectedStepIndex === index;
                            return (
                                <div
                                    key={index}
                                    className={`flex cursor-pointer items-center gap-2 rounded border-2 px-3 py-3 ${
                                        isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:bg-gray-100'
                                    } ${!step.enabled ? 'opacity-50' : ''}`}
                                    onClick={() => onSelectStep(index)}
                                    data-testid={`pipeline-step-item-${index}`}
                                >
                                    <span className="text-xs font-bold text-gray-400">{step.order}</span>
                                    {IconComponent && <IconComponent className="text-base text-blue-500" />}
                                    <span className="flex-1 text-sm font-semibold">{step.label || step.type}</span>
                                    <CheckOutlined className="text-sm font-bold text-green-700" />
                                    <DeleteOutlined
                                        className="text-sm text-red-400 hover:text-red-600"
                                        data-testid={`pipeline-step-delete-${index}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRemoveStep(index);
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Saved Pipelines */}
            <Divider className="m-0" />
            <div className="p-4">
                <h4 className="mb-2 text-sm font-semibold text-gray-600">
                    Saved Pipelines ({savedPipelines.length})
                </h4>
                {savedPipelines.length === 0 ? (
                    <p className="text-sm text-gray-400">No saved pipelines</p>
                ) : (
                    <div className="flex max-h-32 flex-col gap-1 overflow-auto">
                        {savedPipelines.map((pipeline) => (
                            <div
                                key={pipeline.pipelineId}
                                className="flex cursor-pointer items-center justify-between rounded border-2 border-gray-300 px-3 py-3 hover:bg-gray-100"
                                onClick={() => onLoadPipeline(pipeline)}
                            >
                                <div>
                                    <div className="text-sm font-semibold">{pipeline.pipelineName}</div>
                                    <div className="text-xs text-gray-400">{(pipeline.steps || []).length} steps</div>
                                </div>
                                <Popconfirm
                                    title="Delete this pipeline?"
                                    onConfirm={(e) => {
                                        e.stopPropagation();
                                        onDeletePipeline(pipeline.pipelineId);
                                    }}
                                    onCancel={(e) => e.stopPropagation()}
                                >
                                    <DeleteOutlined
                                        className="text-sm text-red-400 hover:text-red-600"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </Popconfirm>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PipelineSidebar;
